import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { LogManager } from '@src/utility/Logger';

import type { Result } from '../common/Result';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';

const logger = LogManager.getLogger('service:SceneDetectionService');

/**
 * Scene detection response from the API
 */
export type SceneDetectionResponse = {
  sceneDetectId: string;
  input: {
    detector_type: string;
    params: Record<string, unknown>;
  };
  stats?: string;
  scenes?: string;
};

/**
 * Parsed scene data from CSV
 */
export type SceneData = {
  sceneNumber: number;
  startTime: number;
  endTime: number;
  startTimecode: string;
  endTimecode: string;
  lengthSeconds: number;
};

/**
 * Scene detection format expected by editor command
 */
export type SceneDetectionForCommand = {
  startTime: number;
  lengthSeconds: number;
};

/**
 * Parameters for creating a new scene detection
 */
export type CreateSceneDetectionParams = {
  detector_type: string;
  [key: string]: unknown;
};

/**
 * Result of starting a scene detection job
 */
export type CreateSceneDetectionResult = {
  invocationId: string;
  sceneDetectId: string;
};

/**
 * Service for fetching and processing scene detections from the API.
 * Stateless service that handles scene detection API calls and CSV parsing.
 */
export class SceneDetectionService {
  private apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || process.env.REACT_APP_API_URL || '';
    logger.debug('SceneDetectionService initialized', { apiUrl: this.apiUrl });
  }

  /**
   * Fetches all scene detections for a story
   * @param storyId - The story ID to fetch detections for
   * @returns Result with array of scene detections or error
   */
  async fetchSceneDetections(
    storyId: string,
  ): Promise<Result<SceneDetectionResponse[]>> {
    if (!storyId) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'Story ID is required',
        },
      };
    }

    try {
      logger.debug('Fetching scene detections', { storyId });
      const response = await authenticatedFetch(
        `${this.apiUrl}/api/story/${storyId}/scenes`,
        { credentials: 'include' },
      );

      if (!response.ok) {
        logger.warn('Failed to fetch scene detections', {
          storyId,
          status: response.status,
        });
        return {
          ok: false,
          error: {
            code: 'EXTERNAL',
            message: `Failed to fetch scene detections: ${response.statusText}`,
            details: { status: response.status },
          },
        };
      }

      const data = await response.json();
      logger.debug('Fetched scene detections', {
        storyId,
        count: Object.keys(data).length,
      });

      const detectionsList: SceneDetectionResponse[] = Object.keys(data).map(
        key => ({
          sceneDetectId: key,
          ...data[key],
        }),
      );

      return { ok: true, value: detectionsList };
    } catch (error) {
      logger.error('Error fetching scene detections', { storyId, error });
      return {
        ok: false,
        error: {
          code: 'EXTERNAL',
          message: 'Failed to fetch scene detections',
          details: error,
        },
      };
    }
  }

  /**
   * Creates a new scene detection job for a story
   * @param storyId - The story ID to create detection for
   * @param params - Detection parameters including detector_type
   * @returns Result with invocationId and sceneDetectId or error
   */
  async createSceneDetection(
    storyId: string,
    params: CreateSceneDetectionParams,
  ): Promise<Result<CreateSceneDetectionResult>> {
    if (!storyId) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'Story ID is required',
        },
      };
    }

    try {
      logger.info('Creating scene detection', { storyId, params });
      const response = await authenticatedFetch(
        `${this.apiUrl}/api/story/${storyId}/create-scenes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(params),
        },
      );

      if (!response.ok) {
        logger.warn('Failed to create scene detection', {
          storyId,
          status: response.status,
        });
        return {
          ok: false,
          error: {
            code: 'EXTERNAL',
            message: `Failed to create scene detection: ${response.statusText}`,
            details: { status: response.status },
          },
        };
      }

      const result = await response.json();
      logger.info('Scene detection created', {
        storyId,
        invocationId: result.invocationId,
        sceneDetectId: result.sceneDetectId,
      });

      return { ok: true, value: result };
    } catch (error) {
      logger.error('Error creating scene detection', { storyId, error });
      return {
        ok: false,
        error: {
          code: 'EXTERNAL',
          message: 'Failed to create scene detection',
          details: error,
        },
      };
    }
  }

  /**
   * Deletes a scene detection
   * @param storyId - The story ID
   * @param sceneDetectId - The scene detection ID to delete
   * @returns Result with success or error
   */
  async deleteSceneDetection(
    storyId: string,
    sceneDetectId: string,
  ): Promise<Result<void>> {
    if (!storyId || !sceneDetectId) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'Story ID and scene detection ID are required',
        },
      };
    }

    try {
      logger.info('Deleting scene detection', { storyId, sceneDetectId });
      const response = await authenticatedFetch(
        `${this.apiUrl}/api/story/${storyId}/delete-scenes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sceneDetectId }),
        },
      );

      if (!response.ok) {
        logger.warn('Failed to delete scene detection', {
          storyId,
          sceneDetectId,
          status: response.status,
        });
        return {
          ok: false,
          error: {
            code: 'EXTERNAL',
            message: `Failed to delete scene detection: ${response.statusText}`,
            details: { status: response.status },
          },
        };
      }

      logger.info('Scene detection deleted', { storyId, sceneDetectId });
      return { ok: true, value: undefined };
    } catch (error) {
      logger.error('Error deleting scene detection', {
        storyId,
        sceneDetectId,
        error,
      });
      return {
        ok: false,
        error: {
          code: 'EXTERNAL',
          message: 'Failed to delete scene detection',
          details: error,
        },
      };
    }
  }

  /**
   * Parses a scene CSV file from a URL
   * @param csvUrl - The URL of the CSV file to parse
   * @returns Result with array of parsed scene data or error
   */
  async parseSceneCSV(csvUrl: string): Promise<Result<SceneData[]>> {
    if (!csvUrl) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'CSV URL is required',
        },
      };
    }

    try {
      logger.debug('Parsing scene CSV', { csvUrl });
      const response = await fetch(csvUrl);

      if (!response.ok) {
        logger.warn('Failed to fetch CSV', { csvUrl, status: response.status });
        return {
          ok: false,
          error: {
            code: 'EXTERNAL',
            message: `Failed to fetch CSV: ${response.statusText}`,
            details: { status: response.status },
          },
        };
      }

      const csvText = await response.text();
      const lines = csvText.trim().split('\n');

      // Skip header line
      const dataLines = lines.slice(1);

      const scenes: SceneData[] = [];
      for (const line of dataLines) {
        if (!line.trim()) continue;

        const parts = line.split(',');
        if (parts.length < 4) continue;

        const sceneNumber = parseInt(parts[0]);
        const startTime = parseFloat(parts[1]);
        const endTime = parseFloat(parts[2]);
        const duration = parseFloat(parts[3]);

        scenes.push({
          sceneNumber,
          startTime,
          endTime,
          startTimecode: '', // Not provided in new format
          endTimecode: '', // Not provided in new format
          lengthSeconds: duration,
        });
      }

      logger.debug('Parsed scene CSV', { csvUrl, sceneCount: scenes.length });
      return { ok: true, value: scenes };
    } catch (error) {
      logger.error('Error parsing scene CSV', { csvUrl, error });
      return {
        ok: false,
        error: {
          code: 'EXTERNAL',
          message: 'Failed to parse scene CSV',
          details: error,
        },
      };
    }
  }

  /**
   * Converts scene data to the format expected by the editor command
   * @param scenes - Array of parsed scene data
   * @returns Array of scene detections for the command
   */
  convertToCommandFormat(scenes: SceneData[]): SceneDetectionForCommand[] {
    return scenes.map(scene => ({
      startTime: scene.startTime,
      lengthSeconds: scene.lengthSeconds,
    }));
  }

  /**
   * Fetches the first available scene detection with scenes for a story
   * @param storyId - The story ID to fetch detections for
   * @returns Result with the first detection that has scenes, or error if none found
   */
  async getFirstAvailableSceneDetection(
    storyId: string,
  ): Promise<Result<SceneDetectionResponse>> {
    const detectionsResult = await this.fetchSceneDetections(storyId);

    if (!detectionsResult.ok) {
      return detectionsResult;
    }

    // Find first detection with scenes defined
    const detectionWithScenes = detectionsResult.value.find(d => d.scenes);

    if (!detectionWithScenes) {
      logger.debug('No scene detections with scenes found', { storyId });
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'No scene detections with scenes found for this story',
        },
      };
    }

    logger.debug('Found first available scene detection', {
      storyId,
      sceneDetectId: detectionWithScenes.sceneDetectId,
    });
    return { ok: true, value: detectionWithScenes };
  }

  /**
   * Gets scene detections in command format for a story (uses first available detection)
   * @param storyId - The story ID to fetch scenes for
   * @returns Result with array of scene detections for the command, or error
   */
  async getScenesForCommand(
    storyId: string,
  ): Promise<Result<SceneDetectionForCommand[]>> {
    const detectionResult = await this.getFirstAvailableSceneDetection(storyId);

    if (!detectionResult.ok) {
      return detectionResult;
    }

    const detection = detectionResult.value;

    if (!detection.scenes) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Detection has no scenes URL',
        },
      };
    }

    const scenesResult = await this.parseSceneCSV(detection.scenes);

    if (!scenesResult.ok) {
      return scenesResult;
    }

    if (scenesResult.value.length === 0) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'No scenes parsed from CSV',
        },
      };
    }

    const commandScenes = this.convertToCommandFormat(scenesResult.value);
    logger.info('Prepared scenes for command', {
      storyId,
      sceneCount: commandScenes.length,
    });

    return { ok: true, value: commandScenes };
  }

  /**
   * Creates scenes from the first available scene detection for a story.
   * Fetches detections and executes the editor command to create scenes.
   * @param storyId - The story ID to create scenes for
   * @param editor - The video editor instance to execute the command
   * @returns Result with success boolean or error
   */
  async createScenesFromDetection(
    storyId: string,
    editor: IVideoEditor,
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
  ): Promise<Result<boolean>> {
    if (!storyId) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'Story ID is required',
        },
      };
    }

    if (!editor) {
      return {
        ok: false,
        error: {
          code: 'PRECONDITION',
          message: 'Editor is required',
        },
      };
    }

    logger.info('Creating scenes from detection', { storyId });
    const scenesResult = await this.getParsedScenes(storyId);

    if (!scenesResult.ok) {
      logger.debug('No scenes available for creation', {
        storyId,
        error: scenesResult.error.message,
      });
      return scenesResult;
    }

    return this.executeSceneCreation(
      editor,
      scenesResult.value,
      { storyId },
      naturalMediaDimensions,
    );
  }

  /**
   * Parses scenes from a detection and creates scenes using the editor
   * @param detection - The scene detection response with scenes URL
   * @param editor - The video editor instance
   * @param naturalMediaDimensions - Optional natural dimensions for computing crop
   * @returns Result with success boolean or error
   */
  async createScenesFromDetectionResponse(
    detection: SceneDetectionResponse,
    editor: IVideoEditor,
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
  ): Promise<Result<boolean>> {
    if (!detection.scenes) {
      return {
        ok: false,
        error: {
          code: 'VALIDATION',
          message: 'Detection has no scenes URL',
        },
      };
    }

    const scenesResult = await this.parseSceneCSV(detection.scenes);
    if (!scenesResult.ok) {
      return scenesResult;
    }

    if (scenesResult.value.length === 0) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'No scenes parsed from CSV',
        },
      };
    }

    return this.executeSceneCreation(
      editor,
      scenesResult.value,
      {
        sceneDetectId: detection.sceneDetectId,
      },
      naturalMediaDimensions,
    );
  }

  /**
   * Gets parsed scenes for a story (fetches detection and parses CSV)
   * @param storyId - The story ID to fetch scenes for
   * @returns Result with parsed SceneData[] or error
   */
  async getParsedScenes(storyId: string): Promise<Result<SceneData[]>> {
    const detectionResult = await this.getFirstAvailableSceneDetection(storyId);

    if (!detectionResult.ok) {
      return detectionResult;
    }

    const detection = detectionResult.value;

    if (!detection.scenes) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'Detection has no scenes URL',
        },
      };
    }

    const scenesResult = await this.parseSceneCSV(detection.scenes);

    if (!scenesResult.ok) {
      return scenesResult;
    }

    if (scenesResult.value.length === 0) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'No scenes parsed from CSV',
        },
      };
    }

    return scenesResult;
  }

  /**
   * Executes the scene creation command with parsed scene data
   * @param editor - The video editor instance
   * @param scenes - Parsed scene data
   * @param context - Logging context (storyId or sceneDetectId)
   * @returns Result with success boolean or error
   */
  private executeSceneCreation(
    editor: IVideoEditor,
    scenes: SceneData[],
    context: { storyId?: string; sceneDetectId?: string },
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
  ): Result<boolean> {
    const sceneDetections = this.convertToCommandFormat(scenes);
    logger.info('Executing scene creation command', {
      ...context,
      sceneCount: sceneDetections.length,
    });

    const success = editor.commandCreateScenesByMediaTime(
      sceneDetections,
      naturalMediaDimensions,
    );

    if (success) {
      logger.info('Successfully created scenes', {
        ...context,
        sceneCount: sceneDetections.length,
      });
      return { ok: true, value: true };
    } else {
      logger.warn('Failed to create scenes', context);
      return {
        ok: false,
        error: {
          code: 'UNKNOWN',
          message: 'Failed to execute scene creation command',
        },
      };
    }
  }
}

/**
 * Fetch scene detections for a story from the API (standalone function for use outside the class)
 * @param storyId - The story ID to fetch detections for
 * @returns Array of scene detections or null if not available
 */
export async function fetchSceneDetectionsApi(
  storyId: string,
): Promise<SceneDetectionResponse[] | null> {
  const service = getSceneDetectionService();
  const result = await service.fetchSceneDetections(storyId);
  return result.ok && result.value.length > 0 ? result.value : null;
}

/**
 * Fetch and parse the first available scene detection for a story.
 * Returns parsed SceneData[] ready for use.
 * @param storyId - The story ID to fetch scenes for
 * @returns Parsed scene data or null if not available
 */
export async function fetchParsedScenesApi(
  storyId: string,
): Promise<SceneData[] | null> {
  const service = getSceneDetectionService();
  const result = await service.getParsedScenes(storyId);
  return result.ok ? result.value : null;
}

// Singleton instance for convenience
let sceneDetectionServiceInstance: SceneDetectionService | null = null;

export function getSceneDetectionService(): SceneDetectionService {
  if (!sceneDetectionServiceInstance) {
    sceneDetectionServiceInstance = new SceneDetectionService();
  }
  return sceneDetectionServiceInstance;
}
